import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import StandardDatePicker from "./StandardDatePicker";
import { selectStyles } from "../helper/SelectStyles";
import { getExpoById, saveExpo, updateExpo } from "../Apis/ExpoAPIs";
import { getManagers, uploadFile } from "../Apis/ManagerAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { getCategories } from "../Apis/CategoryAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getImageUrl } from "../Apis/api";
import { getClientGroups } from "../Apis/ClientGroupAPIs";
import { getExpoBrands } from "../Apis/ExpoBrandAPIs";
import { getKnownSources } from "../Apis/KnownSourceAPIs";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "12px",
};

const defaultCenter = {
  lat: 13.0827,
  lng: 80.2707, // Chennai
};

const libraries = ["places"];

const slugify = (text) => {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const defaultContactText = `HEAD OFFICE
Prompt Tower, Plot No: 324, Ram Nagar South 12th Extension,
Off Radial Road, Near Kamakshi Hospital,
Pallikaranai, Chennai - 600 100, India.

Mobile: +91 95436 68094, +91 93913 91162, +91 99410 08371
E-mail: project2@prompttradefairs.com
prompttradefairs@gmail.com`;

const ExpoFormLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const returnPath = location.state?.from || "/upcoming-expo";

  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    venue: "",
    expoLocation: "",
    location: "",
    interestedLocations: [],
    products: [],
    sources: [],
    employees: [],
    groups: [],
    totalStallNo: "",
    sqftRate: "",
    stallCount: "",
    hallSqFt: "",
    expoType: "Paid",
    visitorSlot: "",
    layoutFile: null,
    brochureFile: null,
    expoImage: null,
    status: true,
    startTime: "10:00 AM",
    endTime: "07:00 PM",
    websiteLink: "",
    isStallBookingEnabled: true,
    isForcedPresent: true,
    contactDetails: defaultContactText,
    slug: "",
    expoBrandId: null,
    expoBanners: [],
    aboutExpo: {
      expoProfile: { header: "", paragraphs: [""] },
      exhibitionHighlights: { title: "", highlights: [{ title: "", description: "" }] },
      exhibitorProfile: { title: "", points: [""] },
      visitorProfile: { title: "", points: [""] },
    },
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [layoutPreview, setLayoutPreview] = useState(null);
  const [brochurePreview, setBrochurePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mapSearchText, setMapSearchText] = useState("");
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const initialCategoryLoaded = useRef(null);

  const handleSourceSearch = (inputValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setSourcesLoading(true);
      const res = await getKnownSources({ search: inputValue, isActive: true });
      if (res.status) {
        const list = res.response?.data || res.response || [];
        setSourceOptions((prevOptions) => {
          const existingSelected = prevOptions.filter((opt) =>
            formData.sources?.includes(opt.value)
          );
          const newOptions = list.map((s) => ({
            value: s._id || s.id,
            label: s.name,
          }));
          const combined = [...existingSelected];
          newOptions.forEach((opt) => {
            if (!combined.some((c) => c.value === opt.value)) {
              combined.push(opt);
            }
          });
          return combined;
        });
      }
      setSourcesLoading(false);
    }, 300);
  };

  // Google Maps logic
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  const [map, setMap] = useState(null);
  const [markerPos, setMarkerPos] = useState(defaultCenter);
  const autocompleteRef = useRef(null);

  const geocodeAddress = useCallback(
    (address) => {
      if (!window.google || !window.google.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0] && results[0].geometry) {
          const loc = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };
          setMarkerPos(loc);
          if (map) map.panTo(loc);
          setMapSearchText(results[0].formatted_address || address);
          setFormData((prev) => ({
            ...prev,
            location: `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`,
          }));
        }
      });
    },
    [map],
  );

  const onMapClick = useCallback(
    (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const loc = { lat, lng };
      setMarkerPos(loc);
      setFormData((prev) => ({
        ...prev,
        location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      }));

      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: loc }, (results, status) => {
          if (status === "OK" && results[0]) {
            const address =
              results[0].formatted_address ||
              `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            setMapSearchText(address);
          }
        });
      }
    },
    [map],
  );

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const loc = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setMarkerPos(loc);
      if (map) map.panTo(loc);
      const address = place.formatted_address || place.name || "";
      setMapSearchText(address);
      setFormData((prev) => ({
        ...prev,
        location: `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`,
      }));
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setBrandsLoading(true);
      const [mgrRes, locRes, groupRes, brandsRes, sourcesRes] = await Promise.all([
        getManagers({ isActive: true, limit: 1000 }),
        getLocations(),
        getClientGroups({ limit: 1000, groupType: "Expo" }),
        getExpoBrands(),
        getKnownSources({ isActive: true }),
      ]);

      if (sourcesRes && sourcesRes.status) {
        const sourcesData = sourcesRes.response?.data || sourcesRes.response || [];
        setSourceOptions(
          Array.isArray(sourcesData)
            ? sourcesData.map((s) => ({ value: s._id || s.id, label: s.name }))
            : []
        );
      }

      if (mgrRes.status) {
        const mgrData = mgrRes.response?.data || mgrRes.response;
        setEmployeeOptions(
          Array.isArray(mgrData)
            ? mgrData.map((m) => ({ value: m._id || m.id, label: m.name }))
            : [],
        );
      }
      if (locRes.status) {
        const locData = locRes.response?.data || locRes.response;
        setLocationOptions(
          Array.isArray(locData)
            ? locData.map((l) => ({
              value: l._id || l.id || l.name,
              label: l.name,
            }))
            : [],
        );
      }
      if (groupRes && groupRes.status) {
        const groupData = groupRes.response?.data || [];
        setGroupOptions(
          Array.isArray(groupData)
            ? groupData.map((g) => ({ value: g._id || g.id, label: g.groupName }))
            : []
        );
      }

      if (brandsRes.status) {
        const brandsData = brandsRes.response?.data || brandsRes.response || [];
        setBrandOptions(
          Array.isArray(brandsData)
            ? brandsData.map((b) => ({
              value: b._id || b.id,
              label: b.years && b.years.length > 0
                ? `${b.name} - (${b.years.map((y) => y.year).join(", ")})`
                : b.name,
            }))
            : []
        );
      }
      setBrandsLoading(false);

      const catRes = await getCategories();
      if (catRes.status) {
        const catData = catRes.response?.data || catRes.response;
        setCategoryOptions(
          Array.isArray(catData)
            ? catData.map((c) => ({ value: c._id || c.id, label: c.name }))
            : [],
        );
      }

      if (isEdit) {
        setLoading(true);
        const expoRes = await getExpoById(id);
        if (expoRes.status && expoRes.response) {
          const expo = expoRes.response?.data || expoRes.response;

          // Correctly extract event manager IDs
          let managerIds = [];
          if (expo.eventManager) {
            if (Array.isArray(expo.eventManager)) {
              managerIds = expo.eventManager
                .map((em) => (typeof em === "object" ? em._id || em.id : em))
                .filter(Boolean);
            } else {
              const singleId =
                typeof expo.eventManager === "object"
                  ? expo.eventManager._id || expo.eventManager.id
                  : expo.eventManager;
              if (singleId) managerIds = [singleId];
            }
          }

          const newFormData = {
            ...expo,
            name: expo.expoName || expo.name || "",
            startDate: expo.startDate ? expo.startDate.split("T")[0] : "",
            endDate: expo.endDate ? expo.endDate.split("T")[0] : "",
            venue: expo.venue || "",
            expoLocation:
              typeof expo.expoLocation === "object"
                ? expo.expoLocation?._id || expo.expoLocation?.id || ""
                : expo.expoLocation || expo.venue || "",
            location:
              typeof expo.location === "object"
                ? `${expo.location.lat}, ${expo.location.lng}`
                : expo.location || "",
            interestedLocations: Array.isArray(expo.interestedLocations)
              ? expo.interestedLocations.map((l) =>
                typeof l === "object" ? l._id || l.id : l,
              )
              : [],
            products: Array.isArray(expo.products)
              ? expo.products.map((p) =>
                typeof p === "object" ? p._id || p.id : p,
              )
              : [],
            sources: Array.isArray(expo.sources)
              ? expo.sources.map((s) =>
                typeof s === "object" ? s._id || s.id : s,
              ).filter(Boolean)
              : [],
            employees: managerIds,
            groups: Array.isArray(expo.groups)
              ? expo.groups.map((g) =>
                typeof g === "object" ? g._id || g.id : g,
              )
              : [],
            sqftRate: expo.sqmRate || expo.sqftRate || "",
            stallCount: expo.numberOfStalls || expo.stallCount || "",
            hallSqFt: expo.hallSqm || expo.hallSqFt || "",
            expoType: expo.entryType || expo.expoType || "Paid",
            visitorSlot: expo.visitorSlot || expo.paidVisitorsSlot || "",
            status: expo.isActive ?? expo.status ?? true,
            startTime: expo.startTime || "10:00 AM",
            endTime: expo.endTime || "07:00 PM",
            websiteLink: expo.websiteLink || "",
            layoutFile: expo.layoutImage || expo.layoutFile || null,
            brochureFile: expo.brochure || expo.brochureFile || null,
            expoImage: expo.expoImage || null,
            expoBanners: Array.isArray(expo.expoBanners) ? expo.expoBanners : [],
            contactDetails: expo.contactDetails || defaultContactText,
            slug: expo.slug || "",
            expoBrandId: expo.expoBrandId
              ? typeof expo.expoBrandId === "object"
                ? expo.expoBrandId._id || expo.expoBrandId.id
                : expo.expoBrandId
              : null,
            aboutExpo: {
              expoProfile: {
                header: expo.aboutExpo?.expoProfile?.header || "",
                paragraphs: expo.aboutExpo?.expoProfile?.paragraphs || [""]
              },
              exhibitionHighlights: {
                title: expo.aboutExpo?.exhibitionHighlights?.title || "",
                highlights: expo.aboutExpo?.exhibitionHighlights?.highlights || [{ title: "", description: "" }]
              },
              exhibitorProfile: {
                title: expo.aboutExpo?.exhibitorProfile?.title || "",
                points: expo.aboutExpo?.exhibitorProfile?.points || [""]
              },
              visitorProfile: {
                title: expo.aboutExpo?.visitorProfile?.title || "",
                points: expo.aboutExpo?.visitorProfile?.points || [""]
              }
            },
          };
          setFormData(newFormData);

          if (Array.isArray(expo.sources) && expo.sources.length > 0) {
            setSourceOptions((prev) => {
              const updated = [...prev];
              expo.sources.forEach((s) => {
                if (typeof s === "object" && (s._id || s.id)) {
                  const sId = s._id || s.id;
                  if (!updated.some((opt) => opt.value === sId)) {
                    updated.push({ value: sId, label: s.name });
                  }
                }
              });
              return updated;
            });
          }

          if (expo.expoImage) {
            setImagePreview(getImageUrl(expo.expoImage));
          }
          const layoutImg = expo.layoutImage || expo.layoutFile;
          if (
            layoutImg &&
            typeof layoutImg === "string" &&
            layoutImg.match(/\.(jpg|jpeg|png|gif|webp)$/i)
          ) {
            setLayoutPreview(getImageUrl(layoutImg));
          }
          const brochureImg = expo.brochure || expo.brochureFile;
          if (
            brochureImg &&
            typeof brochureImg === "string" &&
            brochureImg.match(/\.(jpg|jpeg|png|gif|webp)$/i)
          ) {
            setBrochurePreview(getImageUrl(brochureImg));
          }

          if (typeof expo.location === "object" && expo.location.lat) {
            const loc = {
              lat: parseFloat(expo.location.lat),
              lng: parseFloat(expo.location.lng),
            };
            setMarkerPos(loc);
            setMapSearchText(expo.location.address || "");
          }

          // Set matching category
          if (expo.category) {
            const catId =
              typeof expo.category === "object"
                ? expo.category._id || expo.category.id
                : expo.category;
            const catRes = await getCategories(); // Re-fetch or use fetched data to find label
            if (catRes.status) {
              const catData = catRes.response?.data || catRes.response;
              const options = Array.isArray(catData)
                ? catData.map((c) => ({ value: c._id || c.id, label: c.name }))
                : [];
              const found = options.find((o) => o.value === catId);
              if (found) {
                setSelectedCategory(found);
                initialCategoryLoaded.current = found.value;
              } else if (
                typeof expo.category === "object" &&
                expo.category.name
              ) {
                setSelectedCategory({
                  value: catId,
                  label: expo.category.name,
                });
                initialCategoryLoaded.current = catId;
              }
            }
          }
        }
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [isEdit, id]);

  useEffect(() => {
    const fetchProductsForCategory = async () => {
      if (!selectedCategory) {
        setProductOptions([]);
        return;
      }
      setProductsLoading(true);
      const prodRes = await getProducts({ categoryId: selectedCategory.value });
      if (prodRes.status) {
        const prodData = prodRes.response?.data || prodRes.response;
        const mapped = Array.isArray(prodData)
          ? prodData.map((p) => ({
            value: p._id || p.id,
            label: p.productName,
            categoryId:
              typeof p.categoryId === "object"
                ? p.categoryId?._id || p.categoryId?.id
                : p.categoryId,
          }))
          : [];
        setProductOptions(mapped);

        // Auto-select all products if it's a new expo or a category change
        const shouldAutoSelect = !isEdit || (selectedCategory.value !== initialCategoryLoaded.current);
        if (shouldAutoSelect) {
          setFormData((prev) => ({
            ...prev,
            products: mapped.map((p) => p.value),
          }));
        } else {
          // Reset after initial load so future selections of the same category will trigger auto-select
          initialCategoryLoaded.current = null;
        }
      }
      setProductsLoading(false);
    };
    fetchProductsForCategory();
  }, [selectedCategory, isEdit]);

  const handleAboutExpoChange = (section, field, value, index = null, subField = null) => {
    setFormData((prev) => {
      const updatedAbout = { ...prev.aboutExpo };
      if (!updatedAbout[section]) {
        updatedAbout[section] = {};
      }
      if (index === null) {
        updatedAbout[section][field] = value;
      } else {
        if (!Array.isArray(updatedAbout[section][field])) {
          updatedAbout[section][field] = [];
        }
        if (subField) {
          if (!updatedAbout[section][field][index]) {
            updatedAbout[section][field][index] = {};
          }
          updatedAbout[section][field][index][subField] = value;
        } else {
          updatedAbout[section][field][index] = value;
        }
      }
      return { ...prev, aboutExpo: updatedAbout };
    });
  };

  const addAboutExpoItem = (section, field, defaultValue) => {
    setFormData((prev) => {
      const updatedAbout = { ...prev.aboutExpo };
      if (!updatedAbout[section]) {
        updatedAbout[section] = {};
      }
      const list = Array.isArray(updatedAbout[section][field]) ? updatedAbout[section][field] : [];
      updatedAbout[section][field] = [...list, defaultValue];
      return { ...prev, aboutExpo: updatedAbout };
    });
  };

  const removeAboutExpoItem = (section, field, index) => {
    setFormData((prev) => {
      const updatedAbout = { ...prev.aboutExpo };
      if (updatedAbout[section] && Array.isArray(updatedAbout[section][field])) {
        updatedAbout[section][field] = updatedAbout[section][field].filter((_, idx) => idx !== index);
      }
      return { ...prev, aboutExpo: updatedAbout };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (e.target.type === "number" && value < 0) return;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "name") {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  const handleTimeChange = (type, field, val) => {
    setFormData((prev) => {
      let currentVal = prev[field] || "12:00 AM";
      const [time, ampm] = currentVal.split(" ");
      const [h, m] = time.split(":");

      let newH = h,
        newM = m,
        newAP = ampm;
      if (type === "h") newH = val.padStart(2, "0");
      if (type === "m") newM = val.padStart(2, "0");
      if (type === "ap") newAP = val;

      return { ...prev, [field]: `${newH}:${newM} ${newAP}` };
    });
  };

  const handleFileChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Local preview logic
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (fieldName === "expoImage") setImagePreview(reader.result);
          if (fieldName === "layoutFile") setLayoutPreview(reader.result);
          if (fieldName === "brochureFile") setBrochurePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // Clear preview if PDF or other non-image
        if (fieldName === "layoutFile") setLayoutPreview(null);
        if (fieldName === "brochureFile") setBrochurePreview(null);
      }

      // Instant Upload
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("folder", "expos");
      const uploadRes = await uploadFile(formDataUpload);
      if (uploadRes.status) {
        const filePath = uploadRes.response?.data?.path || uploadRes.response;
        setFormData((prev) => ({ ...prev, [fieldName]: filePath }));
        const label = fieldName
          .replace("File", "")
          .replace(/([A-Z])/g, " $1")
          .trim();
        const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
        toast.success(`${capitalizedLabel} Uploaded Successfully`);
      }
    }
  };

  const handleBannersUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    toast.info("Uploading banners...");
    const uploadedPaths = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`File "${file.name}" is not an image`);
        continue;
      }

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("folder", "expos");
      const uploadRes = await uploadFile(formDataUpload);
      if (uploadRes.status) {
        const filePath = uploadRes.response?.data?.path || uploadRes.response;
        uploadedPaths.push(filePath);
      }
    }

    if (uploadedPaths.length > 0) {
      setFormData((prev) => ({
        ...prev,
        expoBanners: [...(prev.expoBanners || []), ...uploadedPaths]
      }));
      toast.success(`${uploadedPaths.length} Banner(s) uploaded successfully`);
    }
  };

  const removeBanner = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      expoBanners: (prev.expoBanners || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSelectChange = (name, selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setFormData((prev) => ({ ...prev, [name]: values }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Expo Name is Required";
    if (!formData.startDate) newErrors.startDate = "Start Date is Required";
    if (!formData.endDate) newErrors.endDate = "End Date is Required";
    if (!formData.expoLocation?.trim())
      newErrors.expoLocation = "Location is Required";
    if (!mapSearchText?.trim()) newErrors.venue = "Venue is Required";
    if (!formData.sqftRate)
      newErrors.sqftRate = "Per Square Meter Rate is Required";
    if (!formData.stallCount)
      newErrors.stallCount = "Number of Stalls is Required";
    if (!formData.hallSqFt)
      newErrors.hallSqFt = "Overall Square Meter is Required";

    // if (formData.products.length === 0) {
    //   newErrors.products = "At least one product is Required";
    // }

    if (!selectedCategory) {
      newErrors.category = "Category is Required";
    }

    // if (!formData.visitorSlot) {
    //   newErrors.visitorSlot = "Visitor Slots is Required";
    // }

    if (!formData.expoImage && !imagePreview && !isEdit) {
      newErrors.expoImage = "Expo Image is Required";
    }

    if (!formData.employees || formData.employees.length === 0) {
      newErrors.eventManager = "Event Manager is Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);

    const [lat, lng] = (formData.location || "13.0827, 80.2707")
      .split(",")
      .map((v) => parseFloat(v.trim()));

    const payload = {
      expoName: formData.name,
      slug: formData.slug || "",
      venue: mapSearchText,
      expoLocation: formData.expoLocation,
      startDate: formData.startDate,
      endDate: formData.endDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      websiteLink: formData.websiteLink || "",
      hallSqm: parseFloat(formData.hallSqFt) || 0,
      sqmRate: parseFloat(formData.sqftRate) || 0,
      numberOfStalls: parseInt(formData.stallCount) || 0,
      entryType: formData.expoType,
      visitorSlot: parseInt(formData.visitorSlot) || 0,
      expoImage: formData.expoImage,
      layoutImage: formData.layoutFile,
      brochure: formData.brochureFile,
      products: formData.products,
      sources: formData.sources || [],
      category: selectedCategory?.value || "",
      eventManager: formData.employees || [],
      groups: formData.groups || [],
      client: formData.client || "Prompt Trade Fairs",
      isActive: formData.status,
      location: {
        lat: lat || markerPos.lat,
        lng: lng || markerPos.lng,
        address: mapSearchText,
      },
      contactDetails: formData.contactDetails,
      isForcedPresent: true,
      aboutExpo: formData.aboutExpo,
      expoBrandId: formData.expoBrandId || null,
      expoBanners: formData.expoBanners || [],
    };

    let res;
    if (isEdit) {
      res = await updateExpo(id, payload);
    } else {
      res = await saveExpo(payload);
    }

    if (res.status) {
      toast.success(`Expo ${isEdit ? "Updated" : "Added"} Successfully`);
      navigate(returnPath);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <style>{`
        .quill-expo-wrapper .ql-toolbar.ql-snow { border: none; border-bottom: 1px solid #e5e7eb; }
        .quill-expo-wrapper .ql-container.ql-snow { border: none; }
        .quill-expo-wrapper .ql-editor:focus { outline: none; box-shadow: none; }
        .quill-expo-wrapper .ql-editor.ql-blank::before { color: #9ca3af; font-style: normal; }
      `}</style>
      <div
        className="card-header border-bottom px-24 py-16 d-flex align-items-center justify-content-between"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)",
        }}
      >
        <h6 className="text-white pb-2 mb-0" style={{ fontWeight: "700" }}>
          {isEdit ? "EDIT EXPO" : "ADD EXPO"}
        </h6>
        <Link
          to={returnPath}
          className="btn btn-sm d-flex align-items-center gap-1"
          style={{
            backgroundColor: "#f7e7da",
            color: "black",
            fontWeight: "600",
          }}
        >
          <Icon icon="solar:arrow-left-bold" />{" "}
          {returnPath === "/present-expo" ? "Back to Current Expo" : "List Expo"}
        </Link>
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row gy-4">
            {/* Top Section: Expo Image and Basic Details */}
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <label className="form-label fw-bold d-block text-start mb-12">
                Expo Image <span className="text-danger">*</span>
              </label>
              <div className="position-relative d-inline-block">
                <div
                  className={`radius-12 border d-flex justify-content-center align-items-center bg-light mb-16 ${errors.expoImage ? "border-danger" : ""}`}
                  style={{
                    width: "180px",
                    height: "180px",
                    overflow: "hidden",
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Icon
                      icon="solar:gallery-bold"
                      className="text-secondary"
                      style={{ fontSize: "64px" }}
                    />
                  )}
                </div>
                <label
                  htmlFor="expoImage"
                  className="btn btn-outline-primary btn-sm px-32 radius-8 cursor-pointer"
                >
                  Choose
                  <input
                    type="file"
                    id="expoImage"
                    className="d-none"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "expoImage")}
                  />
                </label>
                {errors.expoImage && (
                  <div
                    className="text-danger mt-12"
                    style={{ fontSize: "14px" }}
                  >
                    {errors.expoImage}
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-9">
              <div className="row gy-4">
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold">
                    Expo Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control radius-8 ${errors.name ? "is-invalid" : ""}`}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter Expo Name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold">
                    Public Exhibition Slug
                  </label>
                  <input
                    type="text"
                    name="slug"
                    className="form-control radius-8 bg-neutral-50"
                    value={formData.slug || ""}
                    readOnly
                    placeholder="Generated automatically from Expo Name"
                  />
                  {formData.slug && (
                    <div className="text-primary mt-1 small d-flex flex-column gap-1">
                      <span>Preview (Visitor): <a href={`https://prompttradefairs.com/upcoming-exhibitions/${formData.slug}?formType=visitor`} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>https://prompttradefairs.com/upcoming-exhibitions/{formData.slug}?formType=visitor</a></span>
                      <span>Preview (Stall): <a href={`https://prompttradefairs.com/upcoming-exhibitions/${formData.slug}?formType=stall`} target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>https://prompttradefairs.com/upcoming-exhibitions/{formData.slug}?formType=stall</a></span>
                    </div>
                  )}

                </div>
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold">
                    Location <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={locationOptions}
                    value={
                      locationOptions.find(
                        (opt) =>
                          opt.value === formData.expoLocation ||
                          opt.label === formData.expoLocation,
                      ) ||
                      (formData.expoLocation
                        ? {
                          value: formData.expoLocation,
                          label: formData.expoLocation,
                        }
                        : null)
                    }
                    onChange={(sel) => {
                      const newLocationId = sel?.value || "";
                      setFormData((prev) => ({
                        ...prev,
                        expoLocation: newLocationId,
                      }));
                      if (errors.expoLocation) {
                        setErrors((prev) => ({
                          ...prev,
                          expoLocation: null,
                        }));
                      }
                      if (sel?.label) {
                        geocodeAddress(sel.label);
                      }
                    }}
                    styles={selectStyles()}
                    placeholder="Select Location"
                  />
                  {errors.expoLocation && (
                    <div className="text-danger mt-1 small">
                      {errors.expoLocation}
                    </div>
                  )}
                </div>

                <div className="col-md-3 text-start">
                  <label className="form-label fw-bold">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <StandardDatePicker
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={`form-control radius-8 ${errors.startDate ? "is-invalid" : ""}`}
                    placeholder="DD/MM/YYYY"
                  />
                  {errors.startDate && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.875em" }}
                    >
                      {errors.startDate}
                    </div>
                  )}
                </div>
                <div className="col-md-3 text-start">
                  <label className="form-label fw-bold">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <StandardDatePicker
                    name="endDate"
                    minDate={formData.startDate || undefined}
                    value={formData.endDate}
                    onChange={handleInputChange}
                    placeholder="DD/MM/YYYY"
                    className={`form-control radius-8 ${errors.endDate ? "is-invalid" : ""}`}
                  />
                  {errors.endDate && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.875em" }}
                    >
                      {errors.endDate}
                    </div>
                  )}
                </div>
                <div className="col-md-3 text-start">
                  <label className="form-label fw-bold">Start Time</label>
                  <div className="d-flex gap-1">
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={(formData.startTime || "12:00 AM").split(":")[0]}
                      onChange={(e) =>
                        handleTimeChange("h", "startTime", e.target.value)
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h.toString().padStart(2, "0")}>
                          {h.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={
                        (formData.startTime || "12:00 AM")
                          .split(":")[1]
                          .split(" ")[0]
                      }
                      onChange={(e) =>
                        handleTimeChange("m", "startTime", e.target.value)
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                        <option key={m} value={m.toString().padStart(2, "0")}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={(formData.startTime || "12:00 AM").split(" ")[1]}
                      onChange={(e) =>
                        handleTimeChange("ap", "startTime", e.target.value)
                      }
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-3 text-start">
                  <label className="form-label fw-bold">End Time</label>
                  <div className="d-flex gap-1">
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={(formData.endTime || "12:00 AM").split(":")[0]}
                      onChange={(e) =>
                        handleTimeChange("h", "endTime", e.target.value)
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h.toString().padStart(2, "0")}>
                          {h.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={
                        (formData.endTime || "12:00 AM")
                          .split(":")[1]
                          .split(" ")[0]
                      }
                      onChange={(e) =>
                        handleTimeChange("m", "endTime", e.target.value)
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                        <option key={m} value={m.toString().padStart(2, "0")}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control radius-8 flex-fill px-1 text-center fw-bold"
                      value={(formData.endTime || "12:00 AM").split(" ")[1]}
                      onChange={(e) =>
                        handleTimeChange("ap", "endTime", e.target.value)
                      }
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6 text-start">
                  <label className="form-label fw-bold">Website Link</label>
                  <input
                    type="url"
                    name="websiteLink"
                    className="form-control radius-8"
                    value={formData.websiteLink || ""}
                    onChange={handleInputChange}
                    placeholder="Enter Website Link (Optional)"
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Hall Stats */}
            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">
                Overall Square Meter <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="hallSqFt"
                className={`form-control radius-8 ${errors.hallSqFt ? "is-invalid" : ""}`}
                value={formData.hallSqFt}
                onChange={handleInputChange}
                placeholder="Enter Hall sqm"
                min="0"
                onWheel={(e) => e.target.blur()}
              />
              {errors.hallSqFt && (
                <div className="invalid-feedback">{errors.hallSqFt}</div>
              )}
            </div>
            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">
                Per Square Meter Rate <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="sqftRate"
                className={`form-control radius-8 ${errors.sqftRate ? "is-invalid" : ""}`}
                value={formData.sqftRate}
                onChange={handleInputChange}
                placeholder="₹ Enter rate per Sqm"
                min="0"
                onWheel={(e) => e.target.blur()}
              />
              {errors.sqftRate && (
                <div className="invalid-feedback">{errors.sqftRate}</div>
              )}
            </div>
            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">
                Number of Stalls <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                name="stallCount"
                className={`form-control radius-8 ${errors.stallCount ? "is-invalid" : ""}`}
                value={formData.stallCount}
                onChange={handleInputChange}
                placeholder="Enter stall number"
                min="0"
                onWheel={(e) => e.target.blur()}
              />
              {errors.stallCount && (
                <div className="invalid-feedback">{errors.stallCount}</div>
              )}
            </div>

            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">
                Visitor Entry Type <span className="text-danger">*</span>
              </label>
              <Select
                options={[
                  { value: "Paid", label: "Paid Entry" },
                  { value: "Free", label: "Free Entry" },
                ]}
                value={{
                  value: formData.expoType,
                  label:
                    formData.expoType === "Paid" ? "Paid Entry" : "Free Entry",
                }}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    expoType: val.value,
                  }))
                }
                isSearchable={false}
                styles={selectStyles()}
                placeholder="Select Entry Type"
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
            </div>

            {/* Conditional Visitor Slot Inputs */}
            {/* {formData.expoType === "Paid" && (
              <div className="col-md-6 text-start">
                <label className="form-label fw-bold">
                  Paid Visitors Slot <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="visitorSlot"
                  className={`form-control radius-8 ${errors.visitorSlot ? "is-invalid" : ""}`}
                  value={formData.visitorSlot}
                  onChange={handleInputChange}
                  placeholder="Enter paid slots"
                  min="0"
                  autoComplete="off"
                />
                {errors.visitorSlot && (
                  <div className="invalid-feedback">
                    {errors.visitorSlot}
                  </div>
                )}
              </div>
            )} */}

            {/* File Uploads */}
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">
                Layout / Design ( PDF )
              </label>
              <input
                type="file"
                className="form-control radius-8"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(e, "layoutFile")}
              />
              {(layoutPreview ||
                (formData.layoutFile &&
                  typeof formData.layoutFile === "string" &&
                  formData.layoutFile.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i,
                  ))) && (
                  <div
                    className="mt-12 radius-8 border p-1 bg-light d-flex align-items-center justify-content-center"
                    style={{
                      width: "fit-content",
                      minWidth: "100px",
                      height: "100px",
                    }}
                  >
                    <img
                      src={layoutPreview || getImageUrl(formData.layoutFile)}
                      alt="Layout Preview"
                      className="radius-8"
                      style={{
                        maxHeight: "100%",
                        maxWidth: "150px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              {formData.layoutFile && (
                <span className="text-secondary-light text-sm mt-1 d-block">
                  {typeof formData.layoutFile === "string"
                    ? formData.layoutFile.split("/").pop()
                    : "File attached"}
                </span>
              )}
            </div>
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">Brochure Image / PDF</label>
              <input
                type="file"
                className="form-control radius-8"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(e, "brochureFile")}
              />
              {(brochurePreview ||
                (formData.brochureFile &&
                  typeof formData.brochureFile === "string" &&
                  formData.brochureFile.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i,
                  ))) && (
                  <div
                    className="mt-12 radius-8 border p-1 bg-light d-flex align-items-center justify-content-center"
                    style={{
                      width: "fit-content",
                      minWidth: "100px",
                      height: "100px",
                    }}
                  >
                    <img
                      src={brochurePreview || getImageUrl(formData.brochureFile)}
                      alt="Brochure Preview"
                      className="radius-8"
                      style={{
                        maxHeight: "100%",
                        maxWidth: "150px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              {formData.brochureFile && (
                <span className="text-secondary-light text-sm mt-1 d-block">
                  {typeof formData.brochureFile === "string"
                    ? formData.brochureFile.split("/").pop()
                    : "File attached"}
                </span>
              )}
            </div>

            {/* Select Sources */}
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">Select Sources</label>
              <Select
                isMulti
                isSearchable
                options={sourceOptions}
                value={sourceOptions.filter((opt) =>
                  formData.sources?.includes(opt.value),
                )}
                onChange={(selected) =>
                  handleSelectChange("sources", selected)
                }
                onInputChange={(inputValue, { action }) => {
                  if (action === "input-change") {
                    handleSourceSearch(inputValue);
                  }
                }}
                isLoading={sourcesLoading}
                styles={selectStyles()}
                placeholder="Select Sources"
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
            </div>

            {/* Expo Banners (Optional - Multiple Images) */}
            <div className="col-12 text-start">
              <label className="form-label fw-bold">Expo Banners (Optional - Multiple Images)</label>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-16 mt-8">
                {(formData.expoBanners || []).map((bannerPath, index) => (
                  <div key={index} className="position-relative" style={{ width: "120px", height: "120px" }}>
                    <img
                      src={getImageUrl(bannerPath)}
                      alt={`Banner ${index + 1}`}
                      className="radius-8 border shadow-sm"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => removeBanner(index)}
                      className="btn btn-danger btn-sm position-absolute rounded-circle d-flex align-items-center justify-content-center p-0"
                      style={{ top: "-8px", right: "-8px", width: "24px", height: "24px" }}
                    >
                      <Icon icon="lucide:x" style={{ fontSize: "14px" }} />
                    </button>
                  </div>
                ))}
                <label
                  className="radius-12 border border-dashed d-flex flex-column justify-content-center align-items-center bg-light cursor-pointer"
                  style={{ width: "120px", height: "120px", borderStyle: "dashed", borderColor: "#ced4da" }}
                >
                  <Icon icon="solar:camera-add-bold" className="text-secondary mb-2" style={{ fontSize: "28px" }} />
                  <span className="text-xs text-secondary-light fw-semibold" style={{ fontSize: "11px" }}>Upload Image</span>
                  <input
                    type="file"
                    className="d-none"
                    accept="image/*"
                    multiple
                    onChange={handleBannersUpload}
                  />
                </label>
              </div>
            </div>

            {/* Products Selection */}
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold mb-8">
                Exhibition Type <span className="text-danger">*</span>
              </label>
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={(selected) => {
                  setSelectedCategory(selected);
                  setFormData((prev) => ({ ...prev, products: [] }));
                }}
                styles={selectStyles()}
                placeholder="Select Exhibition type"
                isClearable
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
              {errors.category && (
                <div
                  className="text-danger mt-2"
                  style={{ fontSize: "0.875em" }}
                >
                  {errors.category}
                </div>
              )}
            </div>

            <div className="col-md-6 text-start">
              <label className="form-label fw-bold mb-8">
                Product <span className="text-danger">*</span>
              </label>
              <Select
                isMulti
                options={productOptions}
                value={productOptions.filter((opt) =>
                  formData.products?.includes(opt.value),
                )}
                onChange={(selected) => {
                  const values = selected
                    ? selected.map((opt) => opt.value)
                    : [];
                  setFormData((prev) => ({ ...prev, products: values }));
                }}
                styles={selectStyles()}
                placeholder={
                  selectedCategory
                    ? "Select Products..."
                    : "Select Exhibition Type First"
                }
                noOptionsMessage={() =>
                  "No products are occurring in this category"
                }
                isLoading={productsLoading}
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
              {selectedCategory &&
                !productsLoading &&
                productOptions.length === 0 && (
                  <div
                    className="text-danger mt-2"
                    style={{ fontSize: "0.875em", fontWeight: "600" }}
                  >
                    No products are occurring in this category.
                  </div>
                )}
              {errors.products && (
                <div
                  className="text-danger mt-2"
                  style={{ fontSize: "0.875em" }}
                >
                  {errors.products}
                </div>
              )}
            </div>

            <div className="col-md-6 text-start">
              <label className="form-label fw-bold mb-8">
                Select Database Groups
              </label>
              <Select
                isMulti
                options={groupOptions}
                value={groupOptions.filter((opt) =>
                  formData.groups?.includes(opt.value),
                )}
                onChange={(selected) => {
                  const values = selected
                    ? selected.map((opt) => opt.value)
                    : [];
                  setFormData((prev) => ({ ...prev, groups: values }));
                }}
                styles={selectStyles()}
                placeholder="Select Database Groups..."
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
            </div>

            <div className="col-md-6 text-start">
              <label className="form-label fw-bold mb-8">
                Select & Map Expo Brand <span className="text-secondary-light fw-normal">(Optional)</span>
              </label>
              <Select
                options={brandOptions}
                value={brandOptions.find(opt => opt.value === formData.expoBrandId) || null}
                onChange={(sel) => {
                  setFormData((prev) => ({
                    ...prev,
                    expoBrandId: sel ? sel.value : null
                  }));
                }}
                isSearchable={true}
                isLoading={brandsLoading}
                styles={selectStyles()}
                placeholder="Search & Map Expo Brand (e.g. BUILD EXPO)..."
                isClearable={true}
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
            </div>

            <div className="col-12 mt-24 text-start">
              <div className="row gy-4 align-items-center">
                <div className="col-md-8">
                  <label className="form-label fw-bold mb-8">
                    Select Event Manager <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    options={employeeOptions}
                    value={employeeOptions.filter((opt) =>
                      formData.employees?.includes(opt.value),
                    )}
                    onChange={(selected) =>
                      handleSelectChange("employees", selected)
                    }
                    styles={selectStyles()}
                    placeholder="Select Event Manager"
                    menuPortalTarget={document.body}
                    maxMenuHeight={450}
                  />
                  {errors.eventManager && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "0.875em" }}
                    >
                      {errors.eventManager}
                    </div>
                  )}
                </div>
                <div
                  className="col-md-4 d-flex justify-content-end align-items-center mt-4 mt-md-0"
                  style={{ position: "relative", top: "29px", right: "40px" }}
                >
                  <label
                    className="form-label fw-bold mb-0 me-3"
                    style={{ fontSize: "16px" }}
                  >
                    Status :
                  </label>
                  <div className="d-flex align-items-center gap-4">
                    <div className="form-check d-flex align-items-center gap-2 mb-0">
                      <input
                        className="form-check-input cursor-pointer shadow-none"
                        type="radio"
                        name="status"
                        id="statusActive"
                        checked={formData.status === true}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, status: true }))
                        }
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "0",
                          accentColor: "#ed1c24",
                        }}
                      />
                      <label
                        className="form-check-label fw-bold mb-0 cursor-pointer"
                        htmlFor="statusActive"
                        style={{
                          fontSize: "14px",
                          color: formData.status ? "#ed1c24" : "inherit",
                        }}
                      >
                        Active
                      </label>
                    </div>
                    <div className="form-check d-flex align-items-center gap-2 mb-0">
                      <input
                        className="form-check-input cursor-pointer shadow-none"
                        type="radio"
                        name="status"
                        id="statusInactive"
                        checked={formData.status === false}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, status: false }))
                        }
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "0",
                          accentColor: "#ed1c24",
                        }}
                      />
                      <label
                        className="form-check-label fw-bold mb-0 cursor-pointer"
                        htmlFor="statusInactive"
                        style={{
                          fontSize: "14px",
                          color: !formData.status ? "#ed1c24" : "inherit",
                        }}
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-24 text-start">
                  <div className="border radius-12 p-24 bg-neutral-50">
                    <h5 className="mb-24 fw-bold text-neutral-800 d-flex align-items-center gap-2">
                      <Icon icon="solar:info-circle-bold" style={{ color: "#ed1c24" }} /> ABOUT THE EXPO SECTIONS
                    </h5>

                    {/* 1. Expo Profile */}
                    <div className="card border radius-8 mb-24 overflow-hidden">
                      <div className="card-header bg-light border-bottom py-12 px-16">
                        <h6 className="mb-0 fw-bold text-neutral-800">1. Expo Profile</h6>
                      </div>
                      <div className="card-body py-16 px-20">
                        <div className="mb-16">
                          <label className="form-label fw-semibold">Profile Header / Greeting</label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            value={formData.aboutExpo?.expoProfile?.header || ""}
                            onChange={(e) => handleAboutExpoChange("expoProfile", "header", e.target.value)}
                            placeholder="e.g. Dear Exhibitors,"
                          />
                        </div>
                        <div>
                          <label className="form-label fw-semibold d-flex align-items-center justify-content-between mb-8">
                            <span>Paragraphs</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 py-4 px-12 radius-8"
                              onClick={() => addAboutExpoItem("expoProfile", "paragraphs", "")}
                            >
                              <Icon icon="solar:add-circle-bold" /> Add Paragraph
                            </button>
                          </label>
                          {(formData.aboutExpo?.expoProfile?.paragraphs || []).map((para, idx) => (
                            <div key={idx} className="d-flex align-items-start gap-2 mb-12">
                              <textarea
                                className="form-control radius-8"
                                rows="3"
                                value={para}
                                onChange={(e) => handleAboutExpoChange("expoProfile", "paragraphs", e.target.value, idx)}
                                placeholder={`Paragraph ${idx + 1}`}
                              />
                              {(formData.aboutExpo?.expoProfile?.paragraphs || []).length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm p-8 radius-8 mt-4"
                                  onClick={() => removeAboutExpoItem("expoProfile", "paragraphs", idx)}
                                >
                                  <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "18px" }} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 2. Exhibition Highlights */}
                    <div className="card border radius-8 mb-24 overflow-hidden">
                      <div className="card-header bg-light border-bottom py-12 px-16">
                        <h6 className="mb-0 fw-bold text-neutral-800">2. Exhibition Highlights</h6>
                      </div>
                      <div className="card-body py-16 px-20">
                        <div className="mb-16">
                          <label className="form-label fw-semibold">Highlights Header Title</label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            value={formData.aboutExpo?.exhibitionHighlights?.title || ""}
                            onChange={(e) => handleAboutExpoChange("exhibitionHighlights", "title", e.target.value)}
                            placeholder="e.g. 2. Exhibition Highlights"
                          />
                        </div>
                        <div>
                          <label className="form-label fw-semibold d-flex align-items-center justify-content-between mb-8">
                            <span>Highlights List</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 py-4 px-12 radius-8"
                              onClick={() => addAboutExpoItem("exhibitionHighlights", "highlights", { title: "", description: "" })}
                            >
                              <Icon icon="solar:add-circle-bold" /> Add Highlight Point
                            </button>
                          </label>
                          {(formData.aboutExpo?.exhibitionHighlights?.highlights || []).map((highlight, idx) => (
                            <div key={idx} className="border radius-8 p-12 mb-16 bg-white position-relative">
                              <div className="d-flex justify-content-end mb-12">
                                {(formData.aboutExpo?.exhibitionHighlights?.highlights || []).length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm py-4 px-12 radius-8 d-flex align-items-center gap-1"
                                    onClick={() => removeAboutExpoItem("exhibitionHighlights", "highlights", idx)}
                                  >
                                    <Icon icon="solar:trash-bin-trash-bold" /> Delete Point
                                  </button>
                                )}
                              </div>
                              <div className="row gy-3">
                                <div className="col-12">
                                  <label className="form-label small fw-semibold">Highlight Title / Key</label>
                                  <input
                                    type="text"
                                    className="form-control radius-8"
                                    value={highlight.title || ""}
                                    onChange={(e) => handleAboutExpoChange("exhibitionHighlights", "highlights", e.target.value, idx, "title")}
                                    placeholder="e.g. Proven Expertise"
                                  />
                                </div>
                                <div className="col-12">
                                  <label className="form-label small fw-semibold">Highlight Description / Value</label>
                                  <textarea
                                    className="form-control radius-8"
                                    rows="2"
                                    value={highlight.description || ""}
                                    onChange={(e) => handleAboutExpoChange("exhibitionHighlights", "highlights", e.target.value, idx, "description")}
                                    placeholder="e.g. Prompt Trade Fairs brings with it the rich experience..."
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 3. Exhibitor Profile */}
                    <div className="card border radius-8 mb-24 overflow-hidden">
                      <div className="card-header bg-light border-bottom py-12 px-16">
                        <h6 className="mb-0 fw-bold text-neutral-800">3. Exhibitor Profile</h6>
                      </div>
                      <div className="card-body py-16 px-20">
                        <div className="mb-16">
                          <label className="form-label fw-semibold">Section Subtitle / Description</label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            value={formData.aboutExpo?.exhibitorProfile?.title || ""}
                            onChange={(e) => handleAboutExpoChange("exhibitorProfile", "title", e.target.value)}
                            placeholder="e.g. ABI Expo features a wide-ranging, comprehensive showcase..."
                          />
                        </div>
                        <div>
                          <label className="form-label fw-semibold d-flex align-items-center justify-content-between mb-8">
                            <span>Exhibitor Business Sectors / Points</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 py-4 px-12 radius-8"
                              onClick={() => addAboutExpoItem("exhibitorProfile", "points", "")}
                            >
                              <Icon icon="solar:add-circle-bold" /> Add Exhibitor Sector
                            </button>
                          </label>
                          <div className="row">
                            {(formData.aboutExpo?.exhibitorProfile?.points || []).map((point, idx) => (
                              <div key={idx} className="col-md-6 d-flex align-items-center gap-2 mb-12">
                                <input
                                  type="text"
                                  className="form-control radius-8"
                                  value={point}
                                  onChange={(e) => handleAboutExpoChange("exhibitorProfile", "points", e.target.value, idx)}
                                  placeholder={`Sector Point ${idx + 1}`}
                                />
                                {(formData.aboutExpo?.exhibitorProfile?.points || []).length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm p-8 radius-8"
                                    onClick={() => removeAboutExpoItem("exhibitorProfile", "points", idx)}
                                  >
                                    <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "18px" }} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 4. Visitor Profile */}
                    <div className="card border radius-8 overflow-hidden">
                      <div className="card-header bg-light border-bottom py-12 px-16">
                        <h6 className="mb-0 fw-bold text-neutral-800">4. Visitor Profile</h6>
                      </div>
                      <div className="card-body py-16 px-20">
                        <div className="mb-16">
                          <label className="form-label fw-semibold">Section Subtitle / Description</label>
                          <input
                            type="text"
                            className="form-control radius-8"
                            value={formData.aboutExpo?.visitorProfile?.title || ""}
                            onChange={(e) => handleAboutExpoChange("visitorProfile", "title", e.target.value)}
                            placeholder="e.g. The exhibition attracts a highly targeted audience of..."
                          />
                        </div>
                        <div>
                          <label className="form-label fw-semibold d-flex align-items-center justify-content-between mb-8">
                            <span>Visitor Industries / Points</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 py-4 px-12 radius-8"
                              onClick={() => addAboutExpoItem("visitorProfile", "points", "")}
                            >
                              <Icon icon="solar:add-circle-bold" /> Add Visitor Sector
                            </button>
                          </label>
                          <div className="row">
                            {(formData.aboutExpo?.visitorProfile?.points || []).map((point, idx) => (
                              <div key={idx} className="col-md-6 d-flex align-items-center gap-2 mb-12">
                                <input
                                  type="text"
                                  className="form-control radius-8"
                                  value={point}
                                  onChange={(e) => handleAboutExpoChange("visitorProfile", "points", e.target.value, idx)}
                                  placeholder={`Visitor Point ${idx + 1}`}
                                />
                                {(formData.aboutExpo?.visitorProfile?.points || []).length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm p-8 radius-8"
                                    onClick={() => removeAboutExpoItem("visitorProfile", "points", idx)}
                                  >
                                    <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "18px" }} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Map Section */}
            <div className="col-12 mt-24 text-start">
              <label className="form-label fw-bold mb-12">
                Venue <span className="text-danger">*</span>
              </label>
              <div className="position-relative mb-3">
                <Icon
                  icon="bi:search"
                  className="position-absolute text-secondary"
                  style={{
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                  }}
                />
                {isLoaded ? (
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      autocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={onPlaceChanged}
                  >
                    <input
                      type="text"
                      className={`form-control radius-8 ${errors.venue ? "is-invalid" : ""}`}
                      placeholder="Search for Location..."
                      style={{ paddingLeft: "40px" }}
                      value={mapSearchText}
                      onChange={(e) => setMapSearchText(e.target.value)}
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="Loading search..."
                    style={{ paddingLeft: "40px" }}
                    disabled
                  />
                )}
              </div>
              {errors.venue && (
                <div className="text-danger mt-1 mb-12 small">
                  {errors.venue}
                </div>
              )}
              <div className="radius-12 overflow-hidden border">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={markerPos}
                    zoom={12}
                    onClick={onMapClick}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                  >
                    <Marker position={markerPos} />
                  </GoogleMap>
                ) : (
                  <div
                    style={containerStyle}
                    className="bg-light d-flex justify-content-center align-items-center"
                  >
                    <span>Loading Map...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Details */}
            <div className="col-12 mt-24 text-start">
              <label className="form-label fw-bold mb-12">
                Contact Us (Default Details)
              </label>
              <textarea
                name="contactDetails"
                className={`form-control radius-8 ${errors.contactDetails ? "is-invalid" : ""}`}
                rows="10"
                value={formData.contactDetails}
                onChange={handleInputChange}
                placeholder={defaultContactText}
              ></textarea>
            </div>

            {/* Action Buttons */}
            <div className="col-12 mt-48 d-flex justify-content-end gap-3 px-20">
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                className="btn radius-8 px-40 py-11"
                style={{
                  backgroundColor: "white",
                  color: "#d9534f",
                  fontWeight: "700",
                  border: "1px solid #d9534f",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn radius-8 px-48 py-11"
                disabled={submitting}
                style={{
                  backgroundColor: "#ed1c24",
                  color: "white",
                  fontWeight: "700",
                }}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpoFormLayer;
